import React, { useState } from "react";
import { toast } from "react-toastify";
import {
  Check,
  Pencil,
  RefreshCw,
  ShieldCheck,
  Trash2,
  Undo2,
} from "lucide-react";
import { Button } from "../ui/button";
import {
  useConfirmCbtDraftMutation,
  useDeleteCbtDraftMutation,
  useEditCbtDraftMutation,
  useRecheckCbtDraftMutation,
  useRestoreCbtDraftMutation,
  useRewriteCbtDraftMutation,
} from "../../reactWrapper/Redux/apiSlice";
import { CheckerPill } from "./Pills";
import {
  DELETE_REASONS,
  deleteReasonLabel,
  describeError,
  inputClass,
} from "./cbtQuestionRuns.config";

const actionClass =
  "inline-flex h-8 items-center gap-1.5 rounded-md border bg-card px-2.5 text-[13px] font-medium text-brand-navy hover:bg-brand-navy-100 disabled:opacity-50";

// The fields the edit form changes, in the order they are shown
const FORM_FIELDS = [
  { key: "answer", label: "Correct answer" },
  { key: "wrong_option_1", label: "Wrong option 1" },
  { key: "wrong_option_2", label: "Wrong option 2" },
  { key: "wrong_option_3", label: "Wrong option 3" },
];

/**
 * One AI-written question (a draft).
 *
 *   number    its position in the run (Q1, Q2...)
 *   editable  true while the run is ready or sent back: edit, delete,
 *             restore and confirm are shown
 */
export default function DraftCard({ draft, number, editable }) {
  const [mode, setMode] = useState(null); // null | 'edit' | 'delete' | 'rewrite'
  const [form, setForm] = useState(null);
  const [reason, setReason] = useState("");
  const [rewriteNote, setRewriteNote] = useState("");

  const [editDraft, editState] = useEditCbtDraftMutation();
  const [deleteDraft, deleteState] = useDeleteCbtDraftMutation();
  const [restoreDraft, restoreState] = useRestoreCbtDraftMutation();
  const [confirmDraft, confirmState] = useConfirmCbtDraftMutation();
  const [recheckDraft, recheckState] = useRecheckCbtDraftMutation();
  const [rewriteDraft, rewriteState] = useRewriteCbtDraftMutation();

  const busy =
    editState.isLoading ||
    deleteState.isLoading ||
    restoreState.isLoading ||
    confirmState.isLoading ||
    recheckState.isLoading ||
    rewriteState.isLoading;

  const isDeleted = draft.status === "deleted";
  const needsLook =
    draft.checker_result === "disagreed" || draft.checker_result === "flagged";
  const needsRecheck =
    draft.checker_result === "edited" || draft.checker_result === "unchecked";

  // Run one API call, show the server's message, and close any open form
  async function callApi(request, successMessage) {
    try {
      const response = await request.unwrap();
      toast.success(response.message || successMessage);
      setMode(null);
    } catch (error) {
      toast.error(describeError(error));
    }
  }

  function startEdit() {
    setForm({
      question: draft.question,
      answer: draft.answer,
      wrong_option_1: draft.wrong_option_1,
      wrong_option_2: draft.wrong_option_2,
      wrong_option_3: draft.wrong_option_3,
      explanation: draft.explanation || "",
    });
    setMode("edit");
  }

  function saveEdit(event) {
    event.preventDefault();
    callApi(editDraft({ id: draft.id, body: form }), "Question saved.");
  }

  /* ---------- Edit form ---------- */
  if (mode === "edit" && form) {
    return (
      <article className="rounded-xl border bg-card p-4">
        <span className="font-bold text-brand-navy">Q{number}</span>
        <form onSubmit={saveEdit} className="mt-3 flex flex-col gap-2.5">
          <label className="text-xs font-semibold text-brand-navy">
            Question
          </label>
          <textarea
            className={`${inputClass} h-auto min-h-[80px] py-2`}
            value={form.question}
            onChange={(event) =>
              setForm({ ...form, question: event.target.value })
            }
          />

          {FORM_FIELDS.map((field) => (
            <div key={field.key}>
              <label className="text-xs font-semibold text-brand-navy">
                {field.label}
              </label>
              <input
                className={`${inputClass} mt-1`}
                value={form[field.key]}
                onChange={(event) =>
                  setForm({ ...form, [field.key]: event.target.value })
                }
              />
            </div>
          ))}

          <label className="text-xs font-semibold text-brand-navy">
            Explanation (optional)
          </label>
          <textarea
            className={`${inputClass} h-auto min-h-[60px] py-2`}
            value={form.explanation}
            onChange={(event) =>
              setForm({ ...form, explanation: event.target.value })
            }
          />

          <p className="text-xs text-muted-foreground">
            Saving a change marks the question as edited; the checker's earlier
            verdict no longer applies.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMode(null)}>
              Cancel
            </Button>
            <Button
              type="submit"
              size="sm"
              disabled={busy}
              className="bg-brand-navy text-white hover:bg-brand-navy/90">
              Save question
            </Button>
          </div>
        </form>
      </article>
    );
  }

  /* ---------- Normal view ---------- */
  // The answer is stored first; the reviewer needs to see which one is correct
  const options = [
    { text: draft.answer, correct: true },
    { text: draft.wrong_option_1, correct: false },
    { text: draft.wrong_option_2, correct: false },
    { text: draft.wrong_option_3, correct: false },
  ];

  let cardClass = "rounded-xl border bg-card p-4";
  if (isDeleted) {
    cardClass = `${cardClass} opacity-70`;
  } else if (needsLook) {
    cardClass = `${cardClass} border-amber-300`;
  }

  return (
    <article className={cardClass}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-bold text-brand-navy">Q{number}</span>
        <span className="max-w-full truncate rounded-full bg-brand-navy-100 px-2.5 py-0.5 text-xs text-brand-navy-700">
          {draft.topic}
        </span>
        {!isDeleted && <CheckerPill result={draft.checker_result} />}
        {draft.status === "approved" && (
          <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
            In the exam bank
          </span>
        )}

        {editable && (
          <div className="ml-auto flex flex-wrap gap-1.5">
            {isDeleted ? (
              <button
                type="button"
                className={actionClass}
                disabled={busy}
                onClick={() =>
                  callApi(restoreDraft(draft.id), "Question restored.")
                }>
                <Undo2 className="h-4 w-4" /> Restore
              </button>
            ) : (
              <>
                {needsLook && (
                  <button
                    type="button"
                    className={actionClass}
                    disabled={busy}
                    title="You checked it and our answer is right"
                    onClick={() =>
                      callApi(confirmDraft(draft.id), "Confirmed.")
                    }>
                    <Check className="h-4 w-4" /> Answer is right
                  </button>
                )}
                {needsRecheck && (
                  <button
                    type="button"
                    className={actionClass}
                    disabled={busy}
                    title="Ask the checker about this question again"
                    onClick={() =>
                      callApi(recheckDraft(draft.id), "Checked again.")
                    }>
                    <ShieldCheck className="h-4 w-4" />
                    {recheckState.isLoading ? "Checking…" : "Re-check"}
                  </button>
                )}

                <button
                  type="button"
                  className={actionClass}
                  disabled={busy}
                  onClick={startEdit}>
                  <Pencil className="h-4 w-4" /> Edit
                </button>
                <button
                  type="button"
                  className={actionClass}
                  disabled={busy}
                  title="The AI writes a new question on the same topic"
                  onClick={() => {
                    setRewriteNote("");
                    setMode(mode === "rewrite" ? null : "rewrite");
                  }}>
                  <RefreshCw className="h-4 w-4" /> Rewrite
                </button>

                <button
                  type="button"
                  className={`${actionClass} border-red-200 text-red-700 hover:bg-red-50`}
                  disabled={busy}
                  onClick={() => {
                    setReason("");
                    setMode(mode === "delete" ? null : "delete");
                  }}>
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Asking for a rewrite */}
      {mode === "rewrite" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-muted/60 p-2.5">
          <input
            className={`${inputClass} min-w-0 flex-1`}
            placeholder="What should change? (optional)"
            value={rewriteNote}
            onChange={(event) => setRewriteNote(event.target.value)}
          />
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMode(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={busy}
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              onClick={() =>
                callApi(
                  rewriteDraft({ id: draft.id, reason: rewriteNote }),
                  "Question rewritten.",
                )
              }>
              {rewriteState.isLoading ? "Writing…" : "Rewrite"}
            </Button>
          </div>
        </div>
      )}

      {/* Choosing a delete reason */}
      {mode === "delete" && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-muted/60 p-2.5">
          <span className="text-[13px] font-medium text-brand-navy">
            Why delete it?
          </span>
          {DELETE_REASONS.map((item) => (
            <button
              key={item.value}
              type="button"
              onClick={() => setReason(item.value)}
              className={`rounded-full border px-2.5 py-0.5 text-xs ${
                reason === item.value
                  ? "border-brand-navy bg-brand-navy text-white"
                  : "bg-card"
              }`}>
              {item.label}
            </button>
          ))}
          <div className="ml-auto flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMode(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={busy || !reason}
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={() =>
                callApi(
                  deleteDraft({ id: draft.id, reason: reason }),
                  "Question deleted.",
                )
              }>
              Delete
            </Button>
          </div>
        </div>
      )}

      {/* What the checker said */}
      {!isDeleted && draft.checker_note && (
        <p className="mt-3 rounded-lg bg-muted/60 px-3 py-2 text-[13px] text-foreground">
          <b>Checker:</b> {draft.checker_note}
        </p>
      )}

      <p className="mt-3 max-w-[80ch] text-[15px] text-foreground">
        {draft.question}
      </p>

      <ol className="mt-2.5 grid gap-1.5 md:grid-cols-2">
        {options.map((option, index) => (
          <li
            key={index}
            className={`flex min-w-0 items-start gap-2.5 rounded-lg border px-2.5 py-2 ${
              option.correct ? "border-emerald-300 bg-emerald-50" : ""
            }`}>
            <span className="min-w-0 flex-1 text-sm">{option.text}</span>
            {option.correct && (
              <span className="mt-0.5 text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                Correct
              </span>
            )}
          </li>
        ))}
      </ol>

      {draft.explanation && (
        <details className="mt-2 text-[13px] text-muted-foreground">
          <summary className="w-max cursor-pointer font-medium text-brand-navy-700">
            Why this answer
          </summary>
          <p className="mt-1 max-w-[80ch]">{draft.explanation}</p>
        </details>
      )}

      {isDeleted && (
        <p className="mt-2 text-xs text-red-700">
          Deleted: {deleteReasonLabel(draft.delete_reason)}. It will not be
          added to the exam.
        </p>
      )}
    </article>
  );
}
